import { plural } from '../speak';

/**
 * Where rules are made and read: the Rules view, the marks on the canvas,
 * a field's answer rules and Try a value, worked out and set when, the
 * formula box, the conditions a page or a field shows by, and the map of
 * where answers lead.
 */
export const rulesUi = {
  // ---- the Rules view
  rules: 'Rules',
  groups: {
    branches: 'Where answers lead',
    shows: 'Shows when',
    required: 'Required when',
    readonly: 'Read-only when',
    compute: 'Worked out from',
    set: 'Set when',
    answer: 'Answer rules',
  },
  filter: 'Filter the rules',
  filterPlaceholder: 'Filter by words: a field, a value…',
  count: (n: number) => (n === 1 ? '1 rule on this page.' : `${n} rules on this page.`),
  noRuleSays: (words: string) => `No rule says “${words}”.`,
  noRules: 'No rules yet: pick a field and open Rules.',
  findRule: (name: string, sentence: string) => `Rule: ${name} — ${sentence}`,
  everyRule: 'every rule on the page, in words',
  backToDesigning: 'Back to designing',
  design: 'Design',
  // ---- marks on the canvas
  marks: { sometimes: 'only sometimes', 'worked-out': 'worked out' },
  markCount: (n: number) => (n === 1 ? '1 rule' : `${n} rules`),
  markNames: { sometimes: 'Shown only sometimes', 'worked-out': 'Worked out', answer: 'Answer rules' },
  openTheRules: (name: string) => `${name}: open the rules`,
  // ---- a question's rules, from ⋮
  answerRules: 'Answer rules',
  workedOutFromOthers: 'Worked out from other answers',
  // ---- answer rules
  addARule: 'Add a rule',
  undo: 'Undo',
  checkedAsTyped: 'Checked as people type, and again when they send.',
  shortest: 'Shortest',
  longest: 'Longest',
  any: 'Any',
  endsWith: 'Ends with',
  atLeast: 'At least',
  atMost: 'At most',
  tickAtLeast: 'Tick at least',
  tickAtMost: 'Tick at most',
  looksLike: 'Looks like',
  myPattern: 'A pattern I write',
  pattern: 'Pattern',
  mustHold: 'Must hold',
  mustHoldPlaceholder: 'Ends >= Starts — type a field’s name, or @',
  allowedDates: 'Allowed dates',
  inThePast: 'In the past',
  inTheFuture: 'In the future',
  allowed: 'Allowed',
  whenItDoesNotFit: 'When it does not fit',
  stopsSending: 'Stops sending',
  onlyWarns: 'Only warns',
  message: 'Message when it does not fit',
  messagePlaceholder: 'Said under the question',
  only: 'Only',
  whenChecked: 'When the rule is checked',
  checkedOnlyWhen: 'Checked only when…',
  onlyWhen: 'Only when…',
  removed: (sentence: string) => `Removed “${sentence}”. `,
  removeRule: (sentence: string) => `Remove the rule “${sentence}”`,
  removeNewRule: 'Remove this new rule',
  removeTheRule: 'Remove the rule',
  // ---- Try a value
  tryAValue: 'Try a value',
  wordsIn: 'Words in',
  asWritten: (language: string) => `${language}, as written`,
  alsoRead: 'The rules also read',
  tryIt: 'Try it',
  typeOrPick: 'Type or pick an answer to see what the form says.',
  passes: 'Passes',
  stillSends: 'Still sends',
  /** Why a field cannot take a value here, by what it is. */
  elsewhere: {
    computed: 'worked out from other answers',
    listed: 'whose choices come from the app',
    files: 'which takes files',
    records: 'which needs the app’s records',
  },
  ownElsewhere: (label: string, why: string) => `${label}, ${why}: try its rules with the whole form.`,
  alsoReadsElsewhere: (label: string, why: string) => `Also reads ${label}, ${why}: try it with the whole form.`,
  // ---- worked out and set when
  workedOutFrom: 'Worked out from',
  typeAName: 'Type a field’s name, or @',
  functions: 'Functions',
  shownOnlyTo: 'Shown only to',
  onPaper: 'On paper',
  onPaperHint: 'Printed with the record (Ctrl+P, Print). Untick it to show it on screen only, as an invoice leaves its journal items off paper.',
  shownWhile: 'Shown while',
  shownWhileWays: { always: 'Always', editing: 'Editing', reading: 'Reading' },
  shownWhileHint: 'A part for editing only — a button beside the name, a warning — or for reading only. A form always being edited shows its editing parts.',
  startsWith: 'Starts with',
  startsWithPlaceholder: 'user — the person using it — or a formula',
  startsWithHint: 'A new record’s first value, worked out once; people may change it. user is the person using the form.',
  newLinesStartWith: 'New lines start with',
  madeStartsWith: 'A record made from it starts with',
  madeStartsWithHint: 'Besides the name typed, when Create and edit… or a name typed makes one. Its fields are the other page’s, typed by their names.',
  rolesPlaceholder: 'sales_team.group_sale_manager, !base.group_portal',
  rolesHint: 'People in any of these roles see it; ! before a role hides it from people in that one. Empty, everyone sees it. The app says who holds which — and its server still decides what they may do.',
  /** What each function does, with an example written as a formula is. */
  functionTips: {
    round: 'Rounded to so many decimals: round(price * qty, 2)',
    roundMoney: 'Rounded to a currency’s cents — three places for a dinar, none for a yen: roundMoney(total, currency_id)',
    min: 'The smallest of the values: min(price, 100)',
    max: 'The largest of the values: max(price, 0)',
    abs: 'Without its sign: abs(balance)',
    sum: 'The lines’ field added up, where a condition holds if given: sum(lines, \'amount\', "state == \'done\'")',
    count: 'How many lines, where a condition holds if given: count(lines, "state == \'done\'")',
    average: 'The average of values, or of the lines’ field, the empty ones left out: average(lines, \'price\')',
    today: 'Today’s date, on the page’s clock: today()',
    days: 'The days from one date to another: days(date_opened, today())',
    age: 'Whole years from a birth date to today, or to a date given: age(birth_date) >= 18',
    years: 'Whole years from one date to another: years(start_date, end_date)',
    months: 'Whole months from one date to another, 31 January to 28 February a month: months(start_date, end_date)',
    addDays: 'A date so many days later, earlier when negative: addDays(arrival, nights)',
    addMonths: 'A date so many months later, on a shorter month’s last day: addMonths(first_due, 3)',
    addYears: 'A date so many years later: addYears(start_date, 1)',
    contains: 'Whether text holds some words, whatever their case, or a list holds a value: contains(email, "@gmail.")',
    startsWith: 'Whether text starts with some words, whatever their case: startsWith(phone, "+20")',
    endsWith: 'Whether text ends with some words, whatever their case: endsWith(email, ".gov")',
    upper: 'Text in capitals: upper(code)',
    lower: 'Text in small letters: lower(email)',
    join: 'Values joined with the words first given between them, the empty ones left out: join(" ", first_name, last_name)',
    len: 'How many letters text has, or how many items a list: len(notes)',
    if: 'One value or another: if(qty > 10, 5, 0)',
    empty: 'Whether a value is empty — zero and no are values: empty(phone)',
    firstOf: 'The first value that is not empty: firstOf(nickname, first_name, "friend")',
  },
  /** The functions, by what they work on; the app's own last. */
  functionGroups: { numbers: 'Numbers', dates: 'Dates', words: 'Words', choosing: 'Choosing', app: 'The app’s' },
  /** One of the app's functions that says nothing of itself. */
  appFunctionTip: (name: string) => `The app’s own function: ${name}()`,
  computedHint: 'Worked out as people fill in the form, so they cannot type in it. Empty the box to let them.',
  computeHint: 'A value from other fields, such as Price × Quantity. People then cannot type in it.',
  valuesSetWhen: 'Values set when',
  setWhenAdd: 'Set when…',
  setWhenHint: 'Set as the condition starts to hold. People can still change it.',
  setWhen: 'Set when',
  remove: 'Remove',
  setTo: 'Set to',
  /** A rule set as its condition starts to hold, or when a field changes. */
  setAs: 'Set',
  asItStartsToHold: 'as the condition starts to hold',
  whenChanges: (label: string) => `when “${label}” changes`,
  ifPlaceholder: 'Always — or a condition, such as Quantity > 10',
  yes: 'Yes',
  no: 'No',
  choose: 'Choose…',
  when: 'When',
  whenPlaceholder: 'Quantity > 10 — type a field’s name, or @',
  setToWhen: 'Set to … when …',
  removeSet: (sentence: string) => `Remove: ${sentence}`,
  // ---- the formula box
  fields: 'Fields',
  reads: 'Reads: ',
  // ---- when a part shows, or a field is required or read-only
  show: { page: 'Show this page', question: 'Show this question', group: 'Show this group' },
  required: 'Required',
  readonly: 'Read-only',
  /** "Required when", before the field it tests. */
  whenLead: (lead: string) => `${lead} when`,
  match: 'Match',
  allOfThese: 'all of these',
  anyOfThese: 'any of these',
  /** Around the choice of all or any: "Show this page when [all of these] hold". */
  matchBefore: (lead: string) => `${lead} when`,
  matchAfter: 'hold',
  addACondition: 'Add a condition',
  replace: 'Replace',
  shownByHand: 'Shown when, as written by hand: ',
  byHand: (lead: string) => `${lead} when, as written by hand: `,
  whenShows: { page: 'When this page shows', question: 'When this question shows', group: 'When this group shows' },
  whenItIs: { required: 'When it is required', readonly: 'When it is read-only' },
  condition: (n: number) => `Condition ${n}`,
  whenTheAnswerIs: 'When the answer is',
  answer: (n: number) => `Answer ${n}`,
  removeCondition: (n: number) => `Remove condition ${n}`,
  always: 'Always',
  answerIs: (label: string) => `is ${label}`,
  answerIsNot: (label: string) => `is not ${label}`,
  // ---- where answers lead
  byHandRule: 'a rule written by hand',
  byHandShort: 'a rule by hand',
  notAnswer: (label: string) => `not ${label}`,
  ruleCount: (n: number) => plural('en', n, { one: '# rule', other: '# rules' }),
  mapLabel: (n: number) => `The survey’s ${n} pages, and the answers each page off the line is for`,
  onlyWhenTip: (when: string) => `Only when ${when}`,
  pageOnlyWhen: (label: string, when: string) => `${label}, only when ${when}`,
};
